/**
 * Aura Viva cart guard — Esencia / Infinita, with linked-galleries support.
 *
 * Concepts:
 *   - "Jewel"   = a normal product line item tagged _av_tier and _av_uid.
 *   - "Upgrade" = the Infinita upgrade product, tagged _av_upgrade="true" and
 *                 a matching _av_uid pointing at the group it covers.
 *   - "Group"   = all jewels (and at most one upgrade) sharing the same _av_uid.
 *                 N jewels in one group all share the same gallery URL — that
 *                 is the "linked galleries" feature, used for e.g. a couple's
 *                 set where two physical jewels point to the same gallery.
 *
 * Invariants enforced on every cart change:
 *
 *   (a) Demotion: if any jewel in a group has _av_tier=infinita but the group
 *       has no upgrade, ALL jewels in that group are demoted to esencia.
 *
 *   (b) Orphan upgrade: if an upgrade's group has zero jewels, remove the
 *       upgrade line item.
 *
 *   (c) Duplicate upgrade: if a group has two or more upgrades (can happen
 *       briefly after a "link" action where two source groups merge),
 *       remove the duplicates and keep one. A toast informs the user.
 *
 * Removal-block: prevents the user from removing the LAST jewel of a group
 * that has an upgrade — that would orphan the upgrade. Removing one jewel
 * from a multi-jewel group is fine (the upgrade stays linked to remaining).
 */
(function () {
  "use strict";

  if (window.__AV_CART_TIER_GUARD__) return;
  window.__AV_CART_TIER_GUARD__ = true;

  /* ---- i18n (server-injected via window.__AV_CART_GUARD_I18N__) ---- */

  var I18N = window.__AV_CART_GUARD_I18N__ || {
    remove_block_title: "Action required",
    remove_block_body: "This jewel has a linked Infinita upgrade in your cart. Please remove the upgrade first, then remove the jewel.",
    duplicate_upgrade_toast: "Duplicate Infinita removed — one upgrade covers the shared gallery.",
    modal_ok: "Ok",
  };

  /* ---- cart API ---- */

  async function fetchCart() {
    var r = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    return r.json();
  }

  async function changeLineItem(key, quantity, properties) {
    var body = { id: key, quantity: quantity };
    if (properties) body.properties = properties;
    var r = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
    var data = await r.json().catch(function () { return {}; });
    if (!r.ok) throw new Error((data && (data.description || data.message)) || "Cart change failed");
    return data;
  }

  /* ---- helpers ---- */

  function prop(item, k) { return item && item.properties ? item.properties[k] : undefined; }
  function lower(v)      { return String(v || "").toLowerCase(); }
  function tier(item)    { return lower(prop(item, "_av_tier")); }
  function uid(item)     { var u = prop(item, "_av_uid"); return u ? String(u) : ""; }
  function isUpgrade(item){ return lower(prop(item, "_av_upgrade")) === "true"; }

  function isInfinitaUpgrade(item) {
    return tier(item) === "infinita" && isUpgrade(item) && !!uid(item);
  }
  function isInfinitaJewel(item) {
    return tier(item) === "infinita" && !isUpgrade(item) && !!uid(item);
  }
  function isAnyJewel(item) {
    // A "jewel" = any line item with a uid and no _av_upgrade flag.
    return !!uid(item) && !isUpgrade(item);
  }

  /* ---- modal (delegates to AVModal helper) ---- */

  function showBlockModal() {
    if (window.AVModal && typeof window.AVModal.show === "function") {
      window.AVModal.show({
        title: I18N.remove_block_title,
        body: I18N.remove_block_body,
        okText: I18N.modal_ok,
      });
    } else {
      // Fallback if av-modal.js failed to load
      alert(I18N.remove_block_title + "\n\n" + I18N.remove_block_body);
    }
  }

  function showDuplicateToast() {
    if (window.AVModal && typeof window.AVModal.toast === "function") {
      window.AVModal.toast({ message: I18N.duplicate_upgrade_toast, variant: "info" });
    }
  }

  /* ---- removal blocking ---- */

  // A jewel key is "protected" against removal iff removing it would orphan an
  // upgrade — i.e. it's the LAST jewel in its group AND that group has an upgrade.
  var protectedJewelKeys = new Set();

  async function refreshProtectedKeys() {
    try {
      var cart = await fetchCart();
      var items = cart.items || [];

      // Count jewels per uid
      var jewelCountByUid = new Map();
      for (var i = 0; i < items.length; i++) {
        if (isAnyJewel(items[i])) {
          var u = uid(items[i]);
          jewelCountByUid.set(u, (jewelCountByUid.get(u) || 0) + 1);
        }
      }

      // Collect uids that have an upgrade
      var upgradeUids = new Set();
      for (var j = 0; j < items.length; j++) {
        if (isInfinitaUpgrade(items[j])) upgradeUids.add(uid(items[j]));
      }

      protectedJewelKeys = new Set();
      items.forEach(function (it, k) {
        if (!isAnyJewel(it)) return;
        var u = uid(it);
        if (!upgradeUids.has(u)) return;            // no upgrade → not protected
        if ((jewelCountByUid.get(u) || 0) > 1) return; // others remain → not protected
        protectedJewelKeys.add(it.key);
        protectedJewelKeys.add(String(k + 1));      // some themes use 1-based index
      });
    } catch (e) {
      protectedJewelKeys = new Set();
    }
  }

  function isProtectedRemoval(el) {
    if (!el) return false;
    var cartItem = el.closest("[data-cart-item-key], [data-key], [data-line-item-key], tr, .cart-item");
    if (!cartItem) return false;

    var key = cartItem.getAttribute("data-cart-item-key")
      || cartItem.getAttribute("data-key")
      || cartItem.getAttribute("data-line-item-key")
      || "";
    if (key && protectedJewelKeys.has(key)) return true;

    var idx = el.getAttribute("data-index")
      || el.getAttribute("data-line")
      || cartItem.getAttribute("data-index")
      || cartItem.getAttribute("data-line")
      || "";
    if (idx && protectedJewelKeys.has(String(idx))) return true;

    return false;
  }

  /* ---- enforce (the main invariant checker) ---- */

  async function enforce() {
    var cart = await fetchCart();
    var items = cart.items || [];

    // Group items by uid
    var groupsByUid = new Map();  // uid → { jewels: [], upgrades: [] }
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var u = uid(it);
      if (!u) continue;
      if (!groupsByUid.has(u)) groupsByUid.set(u, { jewels: [], upgrades: [] });
      var g = groupsByUid.get(u);
      if (isUpgrade(it)) g.upgrades.push(it);
      else g.jewels.push(it);
    }

    var toRemove = [];     // [{ key }]
    var toUpdate = [];     // [{ key, qty, props }]
    var duplicateRemoved = false;

    groupsByUid.forEach(function (g) {
      // ── (b) Orphan upgrade: upgrade exists but no jewels ──
      if (g.jewels.length === 0 && g.upgrades.length > 0) {
        for (var u = 0; u < g.upgrades.length; u++) {
          toRemove.push({ key: g.upgrades[u].key });
        }
        return;
      }

      // ── (c) Duplicate upgrade: more than one upgrade for this group ──
      if (g.upgrades.length > 1) {
        for (var d = 1; d < g.upgrades.length; d++) {
          toRemove.push({ key: g.upgrades[d].key });
        }
        duplicateRemoved = true;
        g.upgrades.length = 1; // logical-only, doesn't affect cart
      }

      // ── (a) Demotion: jewel(s) say infinita but group has no upgrade ──
      var hasUpgrade = g.upgrades.length > 0;
      if (!hasUpgrade) {
        for (var k = 0; k < g.jewels.length; k++) {
          var jewel = g.jewels[k];
          if (tier(jewel) === "infinita") {
            var newProps = Object.assign({}, jewel.properties || {});
            newProps._av_tier = "esencia";
            delete newProps._av_upgrade;
            toUpdate.push({ key: jewel.key, qty: jewel.quantity, props: newProps });
          }
        }
      }
    });

    if (!toRemove.length && !toUpdate.length) return false;

    // Apply changes
    for (var r = 0; r < toRemove.length; r++) {
      await changeLineItem(toRemove[r].key, 0);
    }
    for (var p = 0; p < toUpdate.length; p++) {
      await changeLineItem(toUpdate[p].key, toUpdate[p].qty, toUpdate[p].props);
    }

    if (duplicateRemoved) showDuplicateToast();

    return true;
  }

  /* ---- run + event listeners ---- */

  async function run() {
    try {
      var changed = await enforce();
      if (changed) {
        // Notify other scripts (e.g. av-cart-link.js) before the reload
        document.dispatchEvent(new CustomEvent("av:cart-guard-changed"));
        window.location.reload();
        return;
      }
      await refreshProtectedKeys();
      document.dispatchEvent(new CustomEvent("av:cart-guard-ready"));
    } catch (e) {
      console.error("[av-cart-tier-guard]", e);
    }
  }

  function schedule(delay) { setTimeout(run, delay); }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }

  // Block removal of jewels whose removal would orphan an upgrade
  document.addEventListener("click", function (e) {
    var el = e.target && e.target.closest ? e.target.closest("a,button") : null;
    if (!el) return;

    var isRemoveAction = el.matches(
      '[href*="cart/change"], [data-cart-remove], button[name="remove"], .cart-remove-button, cart-remove-button'
    );

    if (isRemoveAction && isProtectedRemoval(el)) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      showBlockModal();
      return;
    }

    if (isRemoveAction || el.matches('button[name="update"]')) {
      schedule(650);
    }
  }, true);

  // Quantity changes
  document.addEventListener("change", function (e) {
    var t = e.target;
    if (!t) return;
    if (t.matches('input[name="updates[]"], input[name^="updates["], .quantity__input, input[data-quantity-input]')) {
      schedule(400);
    }
  });

  // Form submits on cart page
  document.addEventListener("submit", function (e) {
    var form = e.target;
    if (!form) return;
    if (form.matches('form[action^="/cart"]')) {
      schedule(650);
    }
  }, true);

  // Allow av-cart-link.js to request a re-run after a link/unlink action
  document.addEventListener("av:cart-changed", function () {
    schedule(200);
  });
})();
